import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck,
    faCircleExclamation,
    faClockRotateLeft,
    faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from '../FlashMessage';
import { extractApiError } from '../../utils/errorMessages';
import {
    fetchHedgingTasks,
    markHedgingTaskDone,
    reopenHedgingTask,
} from '../../services/paperPricingService';
import { formatCount, formatDateTime, requireList } from './paperPricingFormat';
import { LoadProblem } from './PaperPricingParts';
// The Fixed / Floating pills, exactly as the enquiry list draws them: one idea, one look.
import '../FuelEnquiries/FuelEnquiriesTab.css';

const FILTERS = [
    { value: 'open', label: 'Open' },
    { value: 'done', label: 'Done' },
    { value: 'all', label: 'All' },
];

const EMPTY_TEXT = {
    open: 'No open tasks. A new fixed-versus-floating deal appears here and on your bell.',
    done: 'No finished tasks yet.',
    all: 'No tasks yet. A deal that is fixed on one side and floating on the other appears here.',
};

const UNREADABLE = 'The server sent an answer this screen cannot read. Refresh the page and try again.';

// Every task must carry its content, its lines and both sides of each line. A
// task shown without them would be a false one ("no lines", "not set yet"), so
// a broken answer is a load problem instead.
const checkTask = (task) => {
    if (!task.content || !task.content.enquiry || !task.content.enquiry.url) throw new Error(UNREADABLE);
    requireList(task.content, 'lines').forEach((line) => {
        if (!line.sales || !line.purchase) throw new Error(UNREADABLE);
        requireList(line, 'pricing_periods');
    });
};

const PricingSide = ({ side }) => {
    const pill = side.type === 'fixed'
        ? { className: 'fe-pricing-pill--fixed', label: 'Fixed' }
        : side.type === 'floating'
            ? { className: 'fe-pricing-pill--floating', label: 'Floating' }
            : { className: 'pp-pill--unset', label: 'Not set' };
    return (
        <div className="pp-task-side">
            <span className={`fe-pricing-pill ${pill.className}`}>{pill.label}</span>
            <span className="pp-task-side-text">{side.text}</span>
        </div>
    );
};

const TaskCard = ({ task, onChanged }) => {
    const { content } = task;
    const isOpen = task.status === 'open';
    const [marking, setMarking] = useState(false);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // Under "All" the same card stays on screen after the list reloads, so its
    // own state is reset on success rather than left half-way.
    const markDone = async () => {
        setBusy(true);
        setError('');
        try {
            await markHedgingTaskDone(task.id, note.trim());
            setMarking(false);
            setNote('');
            onChanged(`Marked ${content.enquiry.display_id} done.`);
        } catch (err) {
            setError(extractApiError(err, 'The task could not be marked done. Try again.'));
        } finally {
            setBusy(false);
        }
    };

    const reopen = async () => {
        setBusy(true);
        setError('');
        try {
            await reopenHedgingTask(task.id);
            onChanged(`Reopened the task for ${content.enquiry.display_id}.`);
        } catch (err) {
            setError(extractApiError(err, 'The task could not be reopened. Try again.'));
        } finally {
            setBusy(false);
        }
    };

    const reopenedText = task.reopened_count
        ? ` · Reopened ${task.reopened_count} ${task.reopened_count === 1 ? 'time' : 'times'}`
        : '';

    return (
        <article className={`pp-task ${isOpen ? 'pp-task--open' : 'pp-task--done'}`} aria-label={content.title}>
            <div className="pp-task-head">
                <div className="pp-task-titles">
                    <Link to={content.enquiry.url} className="pp-task-title">{content.title}</Link>
                    <div className="pp-task-meta">
                        {content.customer_label ? <span>Customer: {content.customer_label}</span> : null}
                        <span>Delivery: {content.delivery_text}</span>
                        {content.total_quantity_text ? <span>Total: {content.total_quantity_text}</span> : null}
                    </div>
                </div>
                <span className={`pp-task-status pp-task-status--${isOpen ? 'open' : 'done'}`}>{task.status_label}</span>
            </div>

            {task.changed_note ? (
                <div className="pp-task-changed">
                    <div className="pp-task-changed-title">
                        <FontAwesomeIcon icon={faClockRotateLeft} /> What changed
                    </div>
                    <p className="pp-task-changed-text">{task.changed_note}</p>
                </div>
            ) : null}

            {task.exposure_active === false ? (
                <div className="pp-alert pp-alert--warn">
                    <FontAwesomeIcon icon={faCircleExclamation} className="pp-alert-icon" />
                    <span className="pp-alert-text">
                        The two sides now agree. A hedge may still stand against this deal, so check it before
                        you close the task.
                    </span>
                </div>
            ) : null}

            <div className="pp-table-scroll">
                <table className="pp-table pp-table--task">
                    <thead>
                        <tr>
                            <th scope="col">Product</th>
                            <th scope="col">Purchase</th>
                            <th scope="col">Sale</th>
                            <th scope="col">Quantity</th>
                            <th scope="col">Pricing period</th>
                        </tr>
                    </thead>
                    <tbody>
                        {content.lines.map((line, index) => (
                            // Lines are listed in the enquiry's own order and carry no id.
                            <tr key={`${index}-${line.label}`}>
                                <td className="pp-cell-strong">{line.label}</td>
                                <td><PricingSide side={line.purchase} /></td>
                                <td><PricingSide side={line.sales} /></td>
                                <td className="pp-nowrap">{line.quantity_text}</td>
                                <td>
                                    {line.pricing_periods.length
                                        ? line.pricing_periods.join(', ')
                                        : <span className="pp-muted">Not set yet</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="pp-task-action">
                Action required: <strong>{content.action}</strong>
            </p>

            <div className="pp-task-foot">
                <div className="pp-task-when">
                    {isOpen ? (
                        <>
                            Opened {formatDateTime(task.opened_at)}
                            {task.last_changed_at && task.last_changed_at !== task.opened_at
                                ? ` · Last changed ${formatDateTime(task.last_changed_at)}`
                                : ''}
                            {reopenedText}
                        </>
                    ) : (
                        <>
                            Done{task.done_by_name ? ` by ${task.done_by_name}` : ''} on {formatDateTime(task.done_at)}
                            {reopenedText}
                        </>
                    )}
                    {!isOpen && task.done_note ? <p className="pp-task-note">Note: {task.done_note}</p> : null}
                </div>

                {isOpen && !marking ? (
                    <button
                        type="button"
                        className="pp-btn pp-btn--primary"
                        onClick={() => setMarking(true)}
                        disabled={busy}
                    >
                        <FontAwesomeIcon icon={faCheck} /> Mark done
                    </button>
                ) : null}
                {!isOpen ? (
                    <button type="button" className="pp-btn pp-btn--secondary" onClick={reopen} disabled={busy}>
                        <FontAwesomeIcon icon={faRotateLeft} /> Reopen
                    </button>
                ) : null}
            </div>

            {isOpen && marking ? (
                <div className="pp-task-done-form">
                    <label className="pp-field-label" htmlFor={`pp-task-note-${task.id}`}>
                        Note (optional)
                    </label>
                    <textarea
                        id={`pp-task-note-${task.id}`}
                        className="pp-input pp-textarea"
                        rows={3}
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        placeholder="e.g. Hedged with a September swap, 500 MT at 726.50"
                        disabled={busy}
                    />
                    <div className="pp-task-done-actions">
                        <button
                            type="button"
                            className="pp-btn pp-btn--secondary"
                            onClick={() => {
                                setMarking(false);
                                setNote('');
                            }}
                            disabled={busy}
                        >
                            Cancel
                        </button>
                        <button type="button" className="pp-btn pp-btn--primary" onClick={markDone} disabled={busy}>
                            <FontAwesomeIcon icon={faCheck} /> {busy ? 'Saving…' : 'Mark done'}
                        </button>
                    </div>
                </div>
            ) : null}

            {error ? <div className="pp-alert pp-alert--error" role="alert">{error}</div> : null}
        </article>
    );
};

/**
 * Tasks (ledger row 132, AC27-AC30): one task per enquiry where a product line
 * is fixed on one side and floating on the other. The same task rings the
 * desk's bell; this is its list, with who closed it, when and why.
 *
 * Every sentence on a card (title, pricing texts, quantities, delivery, what
 * changed) is written by the server. This screen shows them and never
 * recomputes a price or a quantity.
 */
const TasksTab = () => {
    const { showSuccess } = useFlashMessage();
    const [status, setStatus] = useState('open');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        fetchHedgingTasks(status)
            .then((answer) => {
                const tasks = requireList(answer, 'results');
                const counts = answer.counts;
                if (!counts || typeof counts.open !== 'number' || typeof counts.done !== 'number') {
                    throw new Error(UNREADABLE);
                }
                tasks.forEach(checkTask);
                if (!cancelled) setData(answer);
            })
            .catch((err) => {
                if (cancelled) return;
                setData(null);
                setError(extractApiError(err, 'The tasks could not be loaded. Try again.'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [status, attempt]);

    const reload = () => setAttempt((count) => count + 1);

    const handleChanged = (message) => {
        showSuccess(message);
        reload();
    };

    const counts = data?.counts;
    const countFor = (value) => (value === 'all' ? counts.open + counts.done : counts[value]);
    const tasks = data ? data.results : [];

    return (
        <div className="pp-panel">
            <div className="pp-panel-head">
                <div>
                    <h2 className="pp-panel-title">Tasks</h2>
                    <p className="pp-panel-subtitle">
                        Deals priced fixed on one side and floating on the other. Each needs a paper trader review.
                    </p>
                </div>
            </div>

            <div className="pp-chips" role="tablist" aria-label="Task status">
                {FILTERS.map((filter) => (
                    <button
                        key={filter.value}
                        type="button"
                        role="tab"
                        aria-selected={status === filter.value}
                        className={`pp-chip ${status === filter.value ? 'pp-chip--active' : ''}`}
                        onClick={() => setStatus(filter.value)}
                    >
                        {filter.label}
                        {counts ? (
                            <>
                                {' '}
                                <span className="pp-chip-count">{formatCount(countFor(filter.value))}</span>
                            </>
                        ) : null}
                    </button>
                ))}
            </div>

            {error ? (
                <LoadProblem message={error} onRetry={reload} />
            ) : loading && !data ? (
                <p className="pp-state">Loading tasks…</p>
            ) : tasks.length === 0 ? (
                <p className="pp-state">{EMPTY_TEXT[status]}</p>
            ) : (
                <div className={`pp-task-list ${loading ? 'pp-dim' : ''}`}>
                    {tasks.map((task) => (
                        <TaskCard key={task.id} task={task} onChanged={handleChanged} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default TasksTab;
